import { useMemo } from "react";
import {
  EnumCashierTxStatus,
  EnumConfirmationStatus,
  type TransactionSummary as Status,
  type EmailVerifyWithdrawalRequestResponse,
  type CancelWithdrawalRequestResponse,
  type CashierTradingAccount,
} from "@/api/cashier";
import { useFormatAmount } from "@/hooks/useFormatAmount";

export interface UseWithdrawalStatusProps {
  verificationParam: string | null;
  statusParam: string | null;
  verificationResult?: EmailVerifyWithdrawalRequestResponse;
  cancelResult?: CancelWithdrawalRequestResponse;
  selectedAccount?: CashierTradingAccount;
  locationState?: Status;
}

export interface UseWithdrawalStatusReturn {
  status?: Status;
  account?: CashierTradingAccount;
  isCancelled: boolean;
}

export const useWithdrawalStatus = ({
  verificationParam,
  statusParam,
  verificationResult,
  cancelResult,
  selectedAccount,
  locationState,
}: UseWithdrawalStatusProps): UseWithdrawalStatusReturn => {
  const { getValueFromBankingAmount } = useFormatAmount();

  return useMemo(() => {
    // Cancelled: use cancelResult (same schema as verificationResult) for both email and OTP flows
    if (statusParam === "cancelled" && cancelResult) {
      const status: Status = {
        money: cancelResult.trading_money,
        balance: selectedAccount
          ? selectedAccount.balance
          : { amount: "", code: "", decimals: 0 },
        status:
          cancelResult.confirmation_status === "approved"
            ? EnumCashierTxStatus.CashierTxComplete
            : EnumCashierTxStatus.CashierTxCancelled,
        external_trading_account_id: selectedAccount?.id ?? "",
      };
      return { status, account: selectedAccount, isCancelled: true };
    }

    // Verified: use verificationResult
    if (verificationParam === "email" && verificationResult) {
      const isCancelled =
        verificationResult.confirmation_status ===
        EnumConfirmationStatus.EnumConfirmationStatusCancelled;

      const status: Status = {
        money: verificationResult.trading_money,
        balance: selectedAccount
          ? selectedAccount.balance
          : { amount: "", code: "", decimals: 0 },
        status: isCancelled
          ? EnumCashierTxStatus.CashierTxCancelled
          : EnumCashierTxStatus.CashierTxComplete,
        external_trading_account_id: selectedAccount?.id ?? "",
      };
      return { status, account: selectedAccount, isCancelled };
    }

    // Non-email: use location state as fallback
    return {
      status: locationState,
      account: selectedAccount,
      isCancelled: false,
    };
  }, [
    verificationParam,
    statusParam,
    verificationResult,
    cancelResult,
    selectedAccount,
    locationState,
    getValueFromBankingAmount,
  ]);
};
